import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  SwfOption,
  buildSwfCreatePayload,
  createSwfRequest,
  fetchAdmissionTypes,
  fetchChildClasses,
  fetchChildNames,
  fetchClaimQuarters,
  fetchCourses,
  fetchDaughterNames,
  fetchDisabilityPercentages,
  fetchDiseases,
  fetchMarriageGiftClaims,
  fetchNoOfChildren,
  fetchPercentageMarks,
  fetchPurposes,
  fetchRelationships,
  fetchSonNames,
  fetchSpouseNames,
  getDocumentTypeForPurpose,
  getEmployeePrefill,
  getLoggedInEmpId,
  getLoggedInUser,
  getMonthOptions,
  getPassedInYearOptions,
  getYearOptions,
  isMarriageClaimFor,
  isSwfSubmitSuccess,
  resolveSwfPurposeCode,
  uploadSwfFile,
} from "./staffWelfare.service";

type UploadedDoc = {
  fileName: string;
  fileExtension: string;
  fileSize: string;
  typeOfAttachedDoc: string;
  documentUploadedBy: string;
  originalName?: string;
};

const CreateStaffWelfare: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;
  const user = getLoggedInUser();
  const empId = getLoggedInEmpId();
  const prefill = getEmployeePrefill(user);

  const L = (key: string, fallback: string) => {
    const v = t(key);
    return v === key ? fallback : v;
  };

  const [form, setForm] = useState({
    name: prefill.name,
    designation: prefill.designation,
    dateOfJoining: prefill.dateOfJoining,
    postcode: prefill.postcode,
    department: "",
    placeOfPosting: "",
    purpose: "",
    claimAmount: "",
    marriageClaimFor: "",
    dateOfMarriage: "",
    nameOfSpouse: "",
    dateOfChildBirth: "",
    nameOfChild: "",
    childOrder: "",
    spouseDeclaration: false,
    dateOfFirstAdmission: "",
    admissionClass: "",
    dateOfResultDeclaration: "",
    class: "",
    percentageOfMarks: "",
    dateOfExpiry: "",
    nameOfPersonExpired: "",
    relationship: "",
    disease: "",
    claimForTheYear: "",
    claimForTheMonth: "",
    claimForTheQuarter: "",
    disabilityAbove40: "",
    disabilityDetails: "",
    course: "",
    courseName: "",
    aggregateMarks: "",
    passedInTheYear: "",
    graduationCourse: "",
    collegeOrUniversity: "",
    nameOfCollegeOrUniversity: "",
    nameOfThePGCourseInWhichAdmission: "",
    dateOfAdmission: "",
    durationOfThePGCourse: "",
    purchaseAmountOfTablet: "",
    dateOfPurchaseOfTablet: "",
    documents: [] as UploadedDoc[],
  });

  const [purposes, setPurposes] = useState<SwfOption[]>([]);
  const [marriageClaims, setMarriageClaims] = useState<SwfOption[]>([]);
  const [childNames, setChildNames] = useState<{ name: string }[]>([]);
  const [spouseNames, setSpouseNames] = useState<{ name: string }[]>([]);
  const [childOrderOptions, setChildOrderOptions] = useState<SwfOption[]>([]);
  const [admissionTypes, setAdmissionTypes] = useState<SwfOption[]>([]);
  const [childClasses, setChildClasses] = useState<SwfOption[]>([]);
  const [percentageMarks, setPercentageMarks] = useState<SwfOption[]>([]);
  const [relationships, setRelationships] = useState<SwfOption[]>([]);
  const [diseases, setDiseases] = useState<SwfOption[]>([]);
  const [disabilityPercentages, setDisabilityPercentages] = useState<SwfOption[]>([]);
  const [quarters, setQuarters] = useState<SwfOption[]>([]);
  const [courses, setCourses] = useState<SwfOption[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [dependentWarning, setDependentWarning] = useState("");

  const yearOptions = getYearOptions();
  const monthOptions = getMonthOptions();
  const passedYearOptions = getPassedInYearOptions();
  const purposeCode = resolveSwfPurposeCode(form.purpose, purposes);
  const isMarg = purposeCode === "MARG";
  const isChbg = purposeCode === "CHBG";
  const isNkgg = purposeCode === "NKGG";
  const isSclg = purposeCode === "SCLG";
  const isFuas = purposeCode === "FUAS";
  const isLosi = purposeCode === "LOSI" || purposeCode === "CANC";
  const isHcpd = purposeCode === "HCPD";
  const isDpgg = purposeCode === "DPGG";
  const isNtab = purposeCode === "NTAB";
  const claimForSon = isMarriageClaimFor(form.marriageClaimFor, "son", marriageClaims);
  const claimForDaughter = isMarriageClaimFor(form.marriageClaimFor, "daughter", marriageClaims);
  const claimForSelf = isMarriageClaimFor(form.marriageClaimFor, "self", marriageClaims);
  const claimForChild = claimForSon || claimForDaughter;

  const setField = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    fetchPurposes()
      .then(setPurposes)
      .catch((err) => toast.error(Api.getErrorMessage(err) || "Failed to fetch purposes"));
  }, []);

  useEffect(() => {
    if (!form.purpose) {
      setDependentWarning("");
      return;
    }
    const code = resolveSwfPurposeCode(form.purpose, purposes);

    const load = async () => {
      try {
        let names: { name: string }[] = [];
        if (code === "MARG") setMarriageClaims(await fetchMarriageGiftClaims());
        if (code === "CHBG") {
          names = await fetchChildNames();
          setChildNames(names);
          setChildOrderOptions(await fetchNoOfChildren());
        }
        if (code === "NKGG") {
          names = await fetchChildNames();
          setChildNames(names);
          setAdmissionTypes(await fetchAdmissionTypes());
        }
        if (code === "SCLG") {
          names = await fetchChildNames();
          setChildNames(names);
          setChildClasses(await fetchChildClasses());
          setPercentageMarks(await fetchPercentageMarks());
        }
        if (code === "FUAS") {
          setRelationships(await fetchRelationships());
        }
        if (code === "LOSI" || code === "CANC") setDiseases(await fetchDiseases());
        if (code === "HCPD") {
          names = await fetchChildNames();
          setChildNames(names);
          setQuarters(await fetchClaimQuarters());
          setDisabilityPercentages(await fetchDisabilityPercentages());
        }
        if (code === "DPGG") {
          names = await fetchDaughterNames();
          setChildNames(names);
          setCourses(await fetchCourses());
        }
        if (code === "NTAB") {
          names = await fetchDaughterNames();
          setChildNames(names);
        }
        if (["CHBG", "NKGG", "SCLG", "HCPD"].includes(code) && names.length === 0) {
          setDependentWarning(
            L(
              "swf_add_child_dependent_warning",
              "Please add child as family member or Dependent in the system through 'Inclusion of Dependents' application"
            )
          );
        } else if (["DPGG", "NTAB"].includes(code) && names.length === 0) {
          setDependentWarning(
            L(
              "swf_add_daughter_dependent_warning",
              "Please add daughter as family member or Dependent in the system through 'Inclusion of Dependents' application"
            )
          );
        } else {
          setDependentWarning("");
        }
      } catch (err: any) {
        toast.error(Api.getErrorMessage(err) || "Failed to load lookup data");
      }
    };
    load();
  }, [form.purpose, purposes]);

  useEffect(() => {
    const load = async () => {
      try {
        if (isMarriageClaimFor(form.marriageClaimFor, "son", marriageClaims)) {
          const names = await fetchSonNames();
          setChildNames(names);
          setDependentWarning(
            names.length
              ? ""
              : L(
                  "swf_add_child_dependent_warning",
                  "Please add child as family member or Dependent in the system through 'Inclusion of Dependents' application"
                )
          );
        } else if (isMarriageClaimFor(form.marriageClaimFor, "daughter", marriageClaims)) {
          const names = await fetchDaughterNames();
          setChildNames(names);
          setDependentWarning(
            names.length
              ? ""
              : L(
                  "swf_add_daughter_dependent_warning",
                  "Please add daughter as family member or Dependent in the system through 'Inclusion of Dependents' application"
                )
          );
        } else if (isMarriageClaimFor(form.marriageClaimFor, "self", marriageClaims)) {
          setSpouseNames(await fetchSpouseNames());
          setDependentWarning("");
        }
      } catch (err: any) {
        toast.error(Api.getErrorMessage(err) || "Failed to load names");
      }
    };
    if (resolveSwfPurposeCode(form.purpose, purposes) === "MARG" && form.marriageClaimFor) load();
  }, [form.purpose, form.marriageClaimFor, purposes, marriageClaims]);

  const handlePurposeChange = (code: string) => {
    setDependentWarning("");
    setForm((prev) => ({
      ...prev,
      purpose: code,
      marriageClaimFor: "",
      dateOfMarriage: "",
      nameOfSpouse: "",
      dateOfChildBirth: "",
      nameOfChild: "",
      childOrder: "",
      spouseDeclaration: false,
      dateOfFirstAdmission: "",
      admissionClass: "",
      dateOfResultDeclaration: "",
      class: "",
      percentageOfMarks: "",
      dateOfExpiry: "",
      nameOfPersonExpired: "",
      relationship: "",
      disease: "",
      claimForTheYear: "",
      claimForTheMonth: "",
      claimForTheQuarter: "",
      disabilityAbove40: "",
      disabilityDetails: "",
      course: "",
      courseName: "",
      aggregateMarks: "",
      passedInTheYear: "",
      graduationCourse: "",
      collegeOrUniversity: "",
      nameOfCollegeOrUniversity: "",
      nameOfThePGCourseInWhichAdmission: "",
      dateOfAdmission: "",
      durationOfThePGCourse: "",
      purchaseAmountOfTablet: "",
      dateOfPurchaseOfTablet: "",
    }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const uploaded = await uploadSwfFile(file);
      setForm((prev) => ({
        ...prev,
        documents: [
          ...prev.documents,
          {
            ...uploaded,
            typeOfAttachedDoc: getDocumentTypeForPurpose(prev.purpose, purposes),
            documentUploadedBy: empId,
            originalName: file.name,
          },
        ],
      }));
      toast.success(t("file_uploaded") || "File uploaded successfully");
    } catch (err: any) {
      toast.error(err?.message || Api.getErrorMessage(err) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const validate = (): string[] => {
    const errors: string[] = [];
    const required: Array<[string, string]> = [
      ["name", L("name", "Name")],
      ["designation", L("designation", "Designation")],
      ["dateOfJoining", L("date_of_joining", "Date of joining")],
      ["postcode", L("payscale", "Payscale")],
      ["department", L("department", "Department")],
      ["placeOfPosting", L("place_of_posting", "Place of posting")],
      ["purpose", L("purpose", "Purpose")],
    ];
    const p = purposeCode;
    if (p === "MARG") {
      required.push(
        ["dateOfMarriage", L("date_of_marriage", "Date of marriage")],
        ["marriageClaimFor", L("claiming_marriage_gift_for", "Claiming Marriage gift for")],
        ["nameOfSpouse", L("name_of_spouse", "Name of Spouse")]
      );
      if (claimForChild) {
        required.push(["nameOfChild", L("name_of_child", "Name of Child")]);
      }
    }
    if (p === "CHBG") {
      required.push(
        ["nameOfChild", L("name_of_child", "Name of Child")],
        ["childOrder", L("child_order", "Child Order (1st/2nd)")]
      );
      if (!form.spouseDeclaration) errors.push(L("spouse_declaration_required", "Spouse declaration is required"));
    }
    if (p === "NKGG") {
      required.push(
        ["dateOfFirstAdmission", L("date_of_first_admission", "Date of 1st Time Admission")],
        ["nameOfChild", L("name_of_child", "Name of Child")],
        ["admissionClass", L("admission_class", "Admission Taken in Class")]
      );
    }
    if (p === "SCLG") {
      required.push(
        ["dateOfResultDeclaration", L("date_of_result_declaration", "Date of Declaration of Result")],
        ["nameOfChild", L("name_of_child", "Name of Child")],
        ["class", L("class", "Class")],
        ["percentageOfMarks", L("percentage_of_marks", "Percentage of Marks")]
      );
    }
    if (p === "FUAS") {
      required.push(
        ["dateOfExpiry", L("date_of_expiry", "Date of Expiry")],
        ["nameOfPersonExpired", L("name_of_person_expired", "Name of Person Expired")],
        ["relationship", L("relationship", "Relationship")]
      );
    }
    if (p === "LOSI" || p === "CANC") {
      required.push(
        ["disease", L("disease", "Disease")],
        ["claimForTheYear", L("claimForTheYear", "Claim for the Year")],
        ["claimForTheMonth", L("claimForTheMonth", "Claim for the Month")]
      );
    }
    if (p === "HCPD") {
      required.push(
        ["nameOfChild", L("name_of_child", "Name of Child")],
        ["disabilityAbove40", L("disability_above_40", "Disability above 40%")],
        ["disabilityDetails", L("disability_details", "Disability Details")],
        ["claimForTheYear", L("claimForTheYear", "Claim for the Year")],
        ["claimForTheQuarter", L("claim_for_the_quarter", "Claim for the Quarter")]
      );
    }
    if (p === "DPGG") {
      required.push(
        ["dateOfResultDeclaration", L("date_of_result_declaration", "Date of Declaration of Result")],
        ["nameOfChild", L("daughter_name", "Name of Daughter")],
        ["course", L("course", "Course")],
        ["courseName", L("name_of_course", "Name of Course")],
        ["aggregateMarks", L("percentage_of_marks", "Percentage of Marks")],
        ["passedInTheYear", L("passed_in_the_year", "Passed in the year")]
      );
    }
    if (p === "NTAB") {
      required.push(
        ["nameOfChild", L("daughter_name", "Name of Daughter")],
        ["graduationCourse", L("graduation_course", "Name of Graduation course completed by Daughter")],
        ["collegeOrUniversity", L("college_or_university", "Name of the College/University from where Graduation was completed")],
        ["aggregateMarks", L("percentage_of_marks", "Percentage of Marks")],
        ["nameOfCollegeOrUniversity", L("pg_college_university", "Name of College/University (PG course)")],
        ["nameOfThePGCourseInWhichAdmission", L("pg_course_name", "Name of the PG Course in which admission taken")],
        ["dateOfAdmission", L("pg_admission_date", "Date of admission in the PG course")],
        ["durationOfThePGCourse", L("pg_course_duration", "Duration of the PG course (in years)")],
        ["dateOfPurchaseOfTablet", L("tablet_purchase_date", "Date of purchase of Tablet PC")],
        ["purchaseAmountOfTablet", L("tablet_cost", "Cost of Tablet PC")]
      );
    }
    required.forEach(([key, label]) => {
      const val = (form as any)[key];
      if (typeof val === "string" ? !val.trim() : !val) {
        errors.push(`${label} is required`);
      }
    });
    if (form.documents.length === 0) {
      errors.push(L("at_least_one_document_is_required", "At least one document is required"));
    } else if (p === "NTAB" && form.documents.length < 3) {
      errors.push(
        L(
          "swf_ntab_three_docs_required",
          "NOTE- All the three documents are to be compulsorily uploaded. In the absence of the same the request would be rejected."
        )
      );
    }
    return errors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validate();
    if (errors.length) {
      toast.error(errors[0]);
      return;
    }
    setShowConfirm(true);
  };

  const confirmSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = buildSwfCreatePayload(form, empId, purposes);
      const res: any = await createSwfRequest(payload);
      if (!isSwfSubmitSuccess(res)) {
        toast.error(res?.message || L("failed_to_submit_request", "Failed to submit request"));
        return;
      }
      setShowConfirm(false);
      setShowThankYou(true);
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || L("failed_to_submit_request", "Failed to submit request"));
    } finally {
      setSubmitting(false);
    }
  };

  const documentHint = () => {
    switch (purposeCode) {
      case "MARG":
        return L(
          "swf_doc_hint_marriage",
          "In case of Marriage gift, marriage card/marriage certificate must be attached"
        );
      case "CHBG":
        return L(
          "swf_doc_hint_child_birth",
          "In case of Child Birth gift, Birth certificate/Discharge summary must be attached"
        );
      case "NKGG":
        return L(
          "swf_doc_hint_nursery",
          "In case of first time admission in Nursery/KG/1st, Admission receipt must be attached"
        );
      case "SCLG":
        return L(
          "swf_doc_hint_school",
          "In case of Grant of assistance for school children, Passing marksheet must be attached"
        );
      case "FUAS":
        return L(
          "swf_doc_hint_funeral",
          "In case of Funeral assistance, Death certificate must be attached"
        );
      case "HCPD":
        return L(
          "attachment_instruction_handicapped_child",
          "In case of Quarterly grant for handicapped child, Disability certificate must be attached"
        );
      case "DPGG":
        return L(
          "attachment_instruction_dependent_daughter",
          "In case of Grant for dependent daughter for degree course, Passing marksheet must be attached"
        );
      case "CANC":
        return L(
          "attachment_instruction_cancer_diet",
          "In case of Diet allowance for employees suffering from cancer, certificate must be attached"
        );
      case "LOSI":
        return L(
          "attachment_instruction_long_sickness",
          "In case of Monthly grant for long sickness, certificate must be attached"
        );
      case "NTAB":
        return L(
          "swf_ntab_three_docs_required",
          "NOTE- All the three documents are to be compulsorily uploaded. In the absence of the same the request would be rejected."
        );
      default:
        return t("upload_documents");
    }
  };

  const attachmentItems = isNtab
    ? [
        L(
          "graduation_marksheet_document",
          "Final Marks sheet of Graduation course of daughter showing marks obtained/final % obtained."
        ),
        L(
          "pg_admission_document",
          "Admission document / ID Card of daughter indicating admission in PG Course."
        ),
        L("tablet_bill_document", "Bill of Tablet PC."),
        L(
          "swf_ntab_three_docs_required",
          "NOTE- All the three documents are to be compulsorily uploaded. In the absence of the same the request would be rejected."
        ),
      ]
    : [
        L(
          "attachment_instruction_marriage",
          "In case of Marriage gift, marriage card/marriage certificate must be attached- in case of Child Birth gift, Birth certificate/ Discharge summary must be attached"
        ),
        L(
          "attachment_instruction_school_children",
          "In case of Grant of assistance for school children, Passing marksheet must be attached- In case of Funeral assistance, Death certificate must be attached"
        ),
        L(
          "attachment_instruction_nursery_admission",
          "In case of first time admission in Nursery/KG/1st, Admission receipt must be attached"
        ),
        L(
          "attachment_instruction_dependent_daughter",
          "In case of Grant for dependent daughter for degree course, Passing marksheet must be attached"
        ),
        L(
          "attachment_instruction_handicapped_child",
          "In case of Quarterly grant for handicapped child, Disability certificate must be attached"
        ),
        L(
          "attachment_instruction_cancer_diet",
          "In case of Diet allowance for employees suffering from cancer, certificate must be attached"
        ),
        L(
          "attachment_instruction_long_sickness",
          "In case of Monthly grant for long sickness, certificate must be attached"
        ),
      ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">
              {L("application_for_staff_welfare_fund", "Application for Staff Welfare Fund")}
            </h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.staffWelfareFund.list}>{t("staff_welfare_fund_grants")}</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t("create_new_request")}
                </li>
              </ol>
            </nav>
          </div>
        </div>

        {dependentWarning ? (
          <div className="alert alert-danger d-flex align-items-start gap-2 mb-3">
            <i className="ti ti-alert-circle fs-5 mt-1"></i>
            <span>{dependentWarning}</span>
          </div>
        ) : null}

        <form onSubmit={handleSubmit}>
          <Section title={t("employee_details")}>
            <div className="row g-3">
              <ReadOnlyField label={L("name", "Name")} value={form.name} required />
              <ReadOnlyField label={L("designation", "Designation")} value={form.designation} required />
              <ReadOnlyField label={L("date_of_joining", "Date of joining")} value={form.dateOfJoining} required />
              <ReadOnlyField label={L("payscale", "Payscale")} value={form.postcode} required />
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  {L("department", "Department")} <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  value={form.department}
                  onChange={(e) => setField("department", e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  {L("place_of_posting", "Place of posting")} <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  value={form.placeOfPosting}
                  onChange={(e) => setField("placeOfPosting", e.target.value)}
                />
              </div>
            </div>
          </Section>

          <Section title={L("purpose", "Purpose")}>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  {L("purpose", "Purpose")} <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={form.purpose}
                  onChange={(e) => handlePurposeChange(e.target.value)}
                >
                  <option value="">{t("select")}</option>
                  {purposes.map((opt) => (
                    <option key={opt.code} value={opt.code}>
                      {opt.description}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">{t("claim_amount")}</label>
                <input className="form-control" value={form.claimAmount} disabled />
              </div>
            </div>
          </Section>

          {isMarg && (
            <Section title={L("marriage_gift", "Marriage Gift")}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("date_of_marriage", "Date of marriage")} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.dateOfMarriage}
                    onChange={(e) => setField("dateOfMarriage", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("claiming_marriage_gift_for", "Claiming Marriage gift for")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.marriageClaimFor}
                    onChange={(e) => {
                      setField("marriageClaimFor", e.target.value);
                      setField("nameOfChild", "");
                      setField("nameOfSpouse", "");
                    }}
                  >
                    <option value="">{t("select")}</option>
                    {marriageClaims.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.description}
                      </option>
                    ))}
                  </select>
                </div>
                {claimForChild && (
                  <>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        {L("name_of_child", "Name of Child")} <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={form.nameOfChild}
                        onChange={(e) => setField("nameOfChild", e.target.value)}
                      >
                        <option value="">{t("select")}</option>
                        {childNames.map((opt) => (
                          <option key={opt.name} value={opt.name}>
                            {opt.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        {claimForSon
                          ? L("name_of_sons_spouse", "Name of Son's Spouse")
                          : L("name_of_daughters_spouse", "Name of Daughter's Spouse")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        className="form-control"
                        value={form.nameOfSpouse}
                        onChange={(e) => setField("nameOfSpouse", e.target.value)}
                      />
                    </div>
                  </>
                )}
                {claimForSelf && (
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      {L("name_of_spouse", "Name of Spouse")} <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={form.nameOfSpouse}
                      onChange={(e) => setField("nameOfSpouse", e.target.value)}
                    >
                      <option value="">{t("select")}</option>
                      {spouseNames.map((opt) => (
                        <option key={opt.name} value={opt.name}>
                          {opt.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </Section>
          )}

          {isChbg && (
            <Section title={L("child_birth_gift", "Child Birth Gift")}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">{L("date_of_child_birth", "Date of Child Birth")}</label>
                  <input type="date" className="form-control" value={form.dateOfChildBirth} disabled />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("name_of_child", "Name of Child")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.nameOfChild}
                    onChange={(e) => setField("nameOfChild", e.target.value)}
                  >
                    <option value="">{t("select")}</option>
                    {childNames.map((opt) => (
                      <option key={opt.name} value={opt.name}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("child_order", "Child Order (1st/2nd)")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.childOrder}
                    onChange={(e) => setField("childOrder", e.target.value)}
                  >
                    <option value="">{t("select")}</option>
                    {childOrderOptions.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <div className="border rounded p-3" style={{ background: "#f3f8f3" }}>
                    <h6 className="fw-bold mb-2">{L("declaration", "Declaration")}</h6>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="spouseDeclaration"
                        checked={form.spouseDeclaration}
                        onChange={(e) => setField("spouseDeclaration", e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="spouseDeclaration">
                        {L(
                          "spouse_declaration_text",
                          "Spouse is not working with DMRC. OR Spouse is not claiming for child birth gift."
                        )}{" "}
                        <span className="text-danger">*</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </Section>
          )}

          {isNkgg && (
            <Section title={t("1st_time_admission_in_nursery_kg_1st")}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("date_of_first_admission", "Date of 1st Time Admission")} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.dateOfFirstAdmission}
                    onChange={(e) => setField("dateOfFirstAdmission", e.target.value)}
                  />
                  <small className="text-muted">
                    {L("swf_date_select_note", "Note: Please choose/enter the DATE.")}
                  </small>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("name_of_child", "Name of Child")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.nameOfChild}
                    onChange={(e) => setField("nameOfChild", e.target.value)}
                  >
                    <option value="">{t("select")}</option>
                    {childNames.map((opt) => (
                      <option key={opt.name} value={opt.name}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("admission_class", "Admission Taken in Class")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.admissionClass}
                    onChange={(e) => setField("admissionClass", e.target.value)}
                  >
                    <option value="">{t("select")}</option>
                    {admissionTypes.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Section>
          )}

          {isSclg && (
            <Section title={L("grant_school_children", "Grant of assistance for school children")}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("date_of_result_declaration", "Date of Declaration of Result")} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.dateOfResultDeclaration}
                    onChange={(e) => setField("dateOfResultDeclaration", e.target.value)}
                  />
                  <small className="text-muted">
                    {L("swf_date_select_note", "Note: Please choose/enter the DATE.")}
                  </small>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("name_of_child", "Name of Child")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.nameOfChild}
                    onChange={(e) => setField("nameOfChild", e.target.value)}
                  >
                    <option value="">{t("select")}</option>
                    {childNames.map((opt) => (
                      <option key={opt.name} value={opt.name}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("class", "Class")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.class}
                    onChange={(e) => setField("class", e.target.value)}
                  >
                    <option value="">{t("select")}</option>
                    {childClasses.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("percentage_of_marks", "Percentage of Marks")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.percentageOfMarks}
                    onChange={(e) => setField("percentageOfMarks", e.target.value)}
                  >
                    <option value="">{t("select")}</option>
                    {percentageMarks.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Section>
          )}

          {isFuas && (
            <Section title={L("funeral_assistance", "Funeral Assistance")}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("date_of_expiry", "Date of Expiry")} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.dateOfExpiry}
                    onChange={(e) => setField("dateOfExpiry", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("name_of_person_expired", "Name of Person Expired")} <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    value={form.nameOfPersonExpired}
                    onChange={(e) => setField("nameOfPersonExpired", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("relationship", "Relationship")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.relationship}
                    onChange={(e) => setField("relationship", e.target.value)}
                  >
                    <option value="">{t("select")}</option>
                    {relationships.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Section>
          )}

          {isLosi && (
            <Section title={isLosi && purposeCode === "CANC" ? L("diet_allowance_cancer", "Diet allowance for employees suffering from cancer") : L("long_sickness", "Monthly grant in case of long sickness")}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("disease", "Disease")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.disease}
                    onChange={(e) => setField("disease", e.target.value)}
                  >
                    <option value="">{t("select")}</option>
                    {diseases.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("claimForTheYear", "Claim for the Year")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.claimForTheYear}
                    onChange={(e) => setField("claimForTheYear", e.target.value)}
                  >
                    <option value="">{t("select")}</option>
                    {yearOptions.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("claimForTheMonth", "Claim for the Month")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.claimForTheMonth}
                    onChange={(e) => setField("claimForTheMonth", e.target.value)}
                  >
                    <option value="">{t("select")}</option>
                    {monthOptions.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Section>
          )}

          {isHcpd && (
            <Section title={L("handicapped_child", "Quarterly grant for handicapped child")}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("name_of_child", "Name of Child")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.nameOfChild}
                    onChange={(e) => setField("nameOfChild", e.target.value)}
                  >
                    <option value="">{t("select")}</option>
                    {childNames.map((opt) => (
                      <option key={opt.name} value={opt.name}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("disability_above_40", "Disability above 40%")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.disabilityAbove40}
                    onChange={(e) => setField("disabilityAbove40", e.target.value)}
                  >
                    <option value="">{t("select")}</option>
                    {disabilityPercentages.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("disability_details", "Disability Details")} <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    value={form.disabilityDetails}
                    onChange={(e) => setField("disabilityDetails", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("claimForTheYear", "Claim for the Year")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.claimForTheYear}
                    onChange={(e) => setField("claimForTheYear", e.target.value)}
                  >
                    <option value="">{t("select")}</option>
                    {yearOptions.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("claim_for_the_quarter", "Claim for the Quarter")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.claimForTheQuarter}
                    onChange={(e) => setField("claimForTheQuarter", e.target.value)}
                  >
                    <option value="">{t("select")}</option>
                    {quarters.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Section>
          )}

          {isDpgg && (
            <Section title={L("dependent_daughter_grant", "Grant for dependent daughter on graduation/post graduation")}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("date_of_result_declaration", "Date of Declaration of Result")} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.dateOfResultDeclaration}
                    onChange={(e) => setField("dateOfResultDeclaration", e.target.value)}
                  />
                  <small className="text-muted">
                    {L("swf_date_select_note", "Note: Please choose/enter the DATE.")}
                  </small>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("daughter_name", "Name of Daughter")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.nameOfChild}
                    onChange={(e) => setField("nameOfChild", e.target.value)}
                  >
                    <option value="">{t("select")}</option>
                    {childNames.map((opt) => (
                      <option key={opt.name} value={opt.name}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("course", "Course")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.course}
                    onChange={(e) => setField("course", e.target.value)}
                  >
                    <option value="">{t("select")}</option>
                    {courses.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("name_of_course", "Name of Course")} <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    value={form.courseName}
                    onChange={(e) => setField("courseName", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("percentage_of_marks", "Percentage of Marks")} <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    value={form.aggregateMarks}
                    onChange={(e) => setField("aggregateMarks", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("passed_in_the_year", "Passed in the year")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.passedInTheYear}
                    onChange={(e) => setField("passedInTheYear", e.target.value)}
                  >
                    <option value="">{t("select")}</option>
                    {passedYearOptions.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Section>
          )}

          {isNtab && (
            <Section title={L("tablet_pc", "Tablet PC to dependent daughter")}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("daughter_name", "Name of Daughter")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.nameOfChild}
                    onChange={(e) => setField("nameOfChild", e.target.value)}
                  >
                    <option value="">{t("select")}</option>
                    {childNames.map((opt) => (
                      <option key={opt.name} value={opt.name}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("graduation_course", "Name of Graduation course completed by Daughter")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    value={form.graduationCourse}
                    onChange={(e) => setField("graduationCourse", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("college_or_university", "Name of the College/University from where Graduation was completed")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    value={form.collegeOrUniversity}
                    onChange={(e) => setField("collegeOrUniversity", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("percentage_of_marks", "Percentage of Marks")} <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    value={form.aggregateMarks}
                    onChange={(e) => setField("aggregateMarks", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("pg_college_university", "Name of College/University (PG course)")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    value={form.nameOfCollegeOrUniversity}
                    onChange={(e) => setField("nameOfCollegeOrUniversity", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("pg_course_name", "Name of the PG Course in which admission taken")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    value={form.nameOfThePGCourseInWhichAdmission}
                    onChange={(e) => setField("nameOfThePGCourseInWhichAdmission", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("pg_admission_date", "Date of admission in the PG course")} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.dateOfAdmission}
                    onChange={(e) => setField("dateOfAdmission", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("pg_course_duration", "Duration of the PG course (in years)")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    type="number"
                    min="0"
                    value={form.durationOfThePGCourse}
                    onChange={(e) => setField("durationOfThePGCourse", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("tablet_purchase_date", "Date of purchase of Tablet PC")} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.dateOfPurchaseOfTablet}
                    onChange={(e) => setField("dateOfPurchaseOfTablet", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {L("tablet_cost", "Cost of Tablet PC")} <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    type="number"
                    min="0"
                    value={form.purchaseAmountOfTablet}
                    onChange={(e) => setField("purchaseAmountOfTablet", e.target.value)}
                  />
                </div>
              </div>
            </Section>
          )}

          <Section title={L("instructions", "Instructions")}>
            <p className="mb-0">
              {L(
                "quarterly_claims_instruction",
                "For Quarterly claims, Quarter and Year are to be chosen according to Calendar year and not financial year"
              )}
            </p>
          </Section>

          <Section title={L("mandatory_attachments", "Mandatory Attachments")}>
            <div className="p-3 rounded" style={{ background: "#fff8d6" }}>
              <ul className="mb-0 ps-3">
                {attachmentItems.map((item, idx) => (
                  <li key={idx} className={idx === attachmentItems.length - 1 && isNtab ? "fw-bold mt-2" : "mb-1"}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Section>

          <Section title={t("upload_documents")}>
            <p className="text-muted small">{documentHint()}</p>
            <div className="d-flex align-items-center gap-2 mb-3">
              <input
                type="file"
                className="form-control"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*"
                onChange={handleFileChange}
                disabled={uploading}
              />
              {uploading && <span className="spinner-border spinner-border-sm" />}
            </div>
            <div className="table-responsive">
              <table className="table table-bordered mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{L("uploaded_documents", "Uploaded Documents")}</th>
                    <th>{L("file_extension_desc", "File Extension Desc.")}</th>
                    <th>{L("file_size_in_kb", "File Size (in KB)")}</th>
                    <th>{t("delete")}</th>
                  </tr>
                </thead>
                <tbody>
                  {form.documents.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-muted text-center">
                        {t("no_records_found")}
                      </td>
                    </tr>
                  ) : (
                    form.documents.map((doc, idx) => (
                      <tr key={`${doc.fileName}-${idx}`}>
                        <td>{doc.originalName || doc.fileName}</td>
                        <td>{doc.fileExtension || doc.typeOfAttachedDoc}</td>
                        <td>{doc.fileSize}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                documents: prev.documents.filter((_, i) => i !== idx),
                              }))
                            }
                          >
                            {t("delete")}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Section>

          <div className="d-flex justify-content-end gap-2 mb-4">
            <Link to={routes.staffWelfareFund.list} className="btn btn-light">
              {t("cancel")}
            </Link>
            <button type="submit" className="btn btn-danger" disabled={submitting || uploading}>
              {t("submit")}
            </button>
          </div>
        </form>

        {showConfirm && (
          <div className="modal fade show d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.45)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t("confirm_submission")}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowConfirm(false)} />
                </div>
                <div className="modal-body">
                  {t("are_you_sure_you_want_to_submit")}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light" disabled={submitting} onClick={() => setShowConfirm(false)}>
                    {t("cancel")}
                  </button>
                  <button type="button" className="btn btn-danger" disabled={submitting} onClick={confirmSubmit}>
                    {submitting ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                    {t("submit")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showThankYou && (
          <div className="modal fade show d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.45)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body text-center py-4">
                  <i className="ti ti-circle-check text-success fs-1 d-block mb-2"></i>
                  <h5>
                    {t(
                      "thank_you_your_staff_welfare_fund_application_has_been_successfully_submitted_or_saved_as_draft."
                    )}
                  </h5>
                  <button
                    className="btn btn-danger mt-3"
                    onClick={() => navigate(routes.staffWelfareFund.list)}
                  >
                    {t("ok") || "OK"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="card border-0 shadow-sm mb-3">
    <div className="card-header bg-white">
      <h5 className="mb-0 fw-bold">{title}</h5>
    </div>
    <div className="card-body">{children}</div>
  </div>
);

const ReadOnlyField: React.FC<{ label: string; value: any; required?: boolean }> = ({
  label,
  value,
  required,
}) => (
  <div className="col-md-6">
    <label className="form-label fw-semibold">
      {label}
      {required ? <span className="text-danger"> *</span> : null}
    </label>
    <input className="form-control" value={value ?? "-"} disabled />
  </div>
);

export default CreateStaffWelfare;
